import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { errDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      setError(errDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FBFBF9] bg-grain flex items-center justify-center px-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-10 justify-center">
          <div className="w-10 h-10 rounded-md bg-[#1F4E3D] flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-display text-2xl font-medium tracking-tight">Kosh</span>
        </div>

        <div className="bg-white border border-[#EAE8E3] rounded-lg p-8 sm:p-10">
          <div className="label-overline mb-2">Sign in</div>
          <h1 className="font-display text-3xl tracking-tight font-light mb-1">Welcome back</h1>
          <p className="text-sm text-[#696E69] mb-8">
            Enter your credentials to reach your ledger.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-[#D26A59] bg-[#D26A59]/8 border border-[#D26A59]/20 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-[#1F4E3D] hover:bg-[#163B2E] text-white rounded-lg py-2.5 h-auto mt-2"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a
              href="/forgot-password"
              data-testid="forgot-password-link"
              className="text-xs text-[#696E69] hover:text-[#1F4E3D] hover:underline"
            >
              Forgot password?
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-[#696E69] mt-6 font-mono-num">
          Don't have an account? Ask your admin.
        </p>
      </div>
    </div>
  );
}
